/*
 * Ported from green hornet's `optimizeFourSquareArt`.
 *
 * MUPS produces a 4-square collage with an expensive upscaled-and-blurred
 * background as the first collage layer. The pattern is:
 *
 *   tag[0] = "USRoot,2800,2800"  (upscale root to 2800x2800)
 *   tag[1] = "BL50"              (blur 50)
 *   tag[2] = "BG34,34,34"        (background fill)
 *   tag[3] = "CL..."             (collage with root as bg + 3 tile images)
 *
 * The first frame of the collage references the upscaled root, which forces
 * Media Central to materialize a 2800x2800 image even when the consumer
 * just needs a 168x168 thumbnail. We detect this exact pattern and:
 *
 *   - drop the four prefix tags
 *   - swap to a `dominantBackground()` fill, which is computed cheaply
 *   - strip the root entry from the collage so only the 3 tile images remain
 *
 * The downstream `scaleToWidth` / `scaleToSquare` calls then size to whatever
 * the consumer actually wants, paying for a 3-tile collage instead of a
 * 4-tile-with-2800-blur monster.
 */
import { first, last, type Maybe } from '@amazon/vinyl-util'
import { type MediaCentralImageBuilder, parseCollageTag } from '@amzn/media-central-url'

const FOUR_SQUARE_PATTERN = ['US', 'BL', 'BG', 'CL']

export function optimizeFourSquareArt(builder: Maybe<MediaCentralImageBuilder>): void {
    if (!builder) return
    const tags = builder.image.tags
    if (
        tags.length !== FOUR_SQUARE_PATTERN.length ||
        !FOUR_SQUARE_PATTERN.every((tagPrefix, i) => tags[i].startsWith(tagPrefix))
    ) {
        return // Not the recognized four-square art pattern
    }

    const collageTagStr = last(tags)
    const collageTag = collageTagStr == null ? null : parseCollageTag(collageTagStr)
    /* v8 ignore next 4 -- required by the nullable return type, but unreachable:
       the pattern guard above already proved the tag starts with 'CL', and past
       that prefix `parseCollageTag` throws rather than returning null. */
    if (!collageTag) {
        // Couldn't be parsed — leave the original tags alone
        return
    }

    const rootId = substringAfterLast(builder.image.base, '/')
    if (first(collageTag.additionalImages) !== `${rootId}.jpg`) {
        // Root image not the first collage entry — pattern may have shifted
        return
    }

    collageTag.boundingBoxes.shift()
    collageTag.additionalImages.shift()
    builder.image.tags = []
    builder.dominantBackground()
    builder.image.tags.push(collageTag.toTagString())
}

function substringAfterLast(value: string, separator: string): string {
    const i = value.lastIndexOf(separator)
    return i < 0 ? value : value.slice(i + separator.length)
}
